import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { PaymentState } from "./types";
import {
  createBehaviourEvent,
  createMyPaymentIntent,
  getCustomerQuota,
  getFeaturesByPlanId,
  getProductGroupByPromoCode,
  getProductGroupContent,
  getProductGroupDefault,
  redeemCredit,
} from "./thunks";

export const name = "payment";

const initialState: PaymentState = {
  customerQuota: { data: undefined, status: "idle" },
  productGroupDefault: {
    data: null,
    status: "idle",
  },
  productGroupByPromoCode: {
    data: null,
    status: "idle",
  },
  featuresByPlanId: {
    data: [],
    status: "idle",
  },
  createMyPaymentIntent: {
    data: null,
    status: "idle",
  },
  createBehaviourEvent: {
    data: null,
    status: "idle",
  },
  redeemCredit: { status: "idle" },
  productGroupContent: {
    data: null,
    status: "idle",
  },
};

export const asyncActions = {
  getCustomerQuotaAsync: createAsyncThunk(
    `${name}/getCustomerQuota`,
    getCustomerQuota
  ),
  getProductGroupDefaultAsync: createAsyncThunk(
    `${name}/getProductGroupDefault`,
    getProductGroupDefault
  ),
  getProductGroupByPromoCodeAsync: createAsyncThunk(
    `${name}/getProductGroupByPromoCode`,
    getProductGroupByPromoCode
  ),
  getFeaturesByPlanIdAsync: createAsyncThunk(
    `${name}/getFeaturesByPlanId`,
    getFeaturesByPlanId
  ),
  createMyPaymentIntentAsync: createAsyncThunk(
    `${name}/createMyPaymentIntent`,
    createMyPaymentIntent
  ),
  createBehaviourEventAsync: createAsyncThunk(
    `${name}/createBehaviourEvent`,
    createBehaviourEvent
  ),
  redeemCreditAsync: createAsyncThunk(
    `${name}/Allowings/redeem-credit`,
    redeemCredit
  ),
  getProductGroupContentAsync: createAsyncThunk(
    `${name}/getProductGroupContent`,
    getProductGroupContent
  ),
};

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clearProductGroupByPromoCode: (state) => {
      state.productGroupByPromoCode = initialState.productGroupByPromoCode;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(asyncActions.getCustomerQuotaAsync.pending, (state) => {
        state.customerQuota.status = "loading";
      })
      .addCase(
        asyncActions.getCustomerQuotaAsync.fulfilled,
        (state, action) => {
          state.customerQuota.data = action.payload;
          state.customerQuota.status = "success";
        }
      )
      .addCase(asyncActions.getCustomerQuotaAsync.rejected, (state) => {
        state.customerQuota.status = "failed";
      })
      .addCase(asyncActions.getProductGroupDefaultAsync.pending, (state) => {
        state.productGroupDefault.status = "loading";
      })
      .addCase(
        asyncActions.getProductGroupDefaultAsync.fulfilled,
        (state, action) => {
          state.productGroupDefault = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(asyncActions.getProductGroupDefaultAsync.rejected, (state) => {
        state.productGroupDefault.status = "failed";
      })
      .addCase(
        asyncActions.getProductGroupByPromoCodeAsync.pending,
        (state) => {
          state.productGroupByPromoCode.status = "loading";
        }
      )
      .addCase(
        asyncActions.getProductGroupByPromoCodeAsync.fulfilled,
        (state, action) => {
          state.productGroupByPromoCode = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(
        asyncActions.getProductGroupByPromoCodeAsync.rejected,
        (state) => {
          state.productGroupByPromoCode.status = "failed";
        }
      )
      .addCase(asyncActions.getFeaturesByPlanIdAsync.pending, (state) => {
        state.featuresByPlanId.status = "loading";
      })
      .addCase(
        asyncActions.getFeaturesByPlanIdAsync.fulfilled,
        (state, action) => {
          state.featuresByPlanId = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(asyncActions.getFeaturesByPlanIdAsync.rejected, (state) => {
        state.featuresByPlanId.status = "failed";
      })
      .addCase(asyncActions.createMyPaymentIntentAsync.pending, (state) => {
        state.createMyPaymentIntent.status = "loading";
      })
      .addCase(
        asyncActions.createMyPaymentIntentAsync.fulfilled,
        (state, action) => {
          state.createMyPaymentIntent = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(asyncActions.createMyPaymentIntentAsync.rejected, (state) => {
        state.createMyPaymentIntent.status = "failed";
      })
      .addCase(asyncActions.createBehaviourEventAsync.pending, (state) => {
        state.createBehaviourEvent.status = "loading";
      })
      .addCase(
        asyncActions.createBehaviourEventAsync.fulfilled,
        (state, action) => {
          state.createBehaviourEvent = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(asyncActions.createBehaviourEventAsync.rejected, (state) => {
        state.createBehaviourEvent.status = "failed";
      })
      .addCase(asyncActions.redeemCreditAsync.pending, (state) => {
        state.redeemCredit.status = "loading";
      })
      .addCase(asyncActions.redeemCreditAsync.fulfilled, (state, action) => {
        state.redeemCredit.status = "success";
      })
      .addCase(asyncActions.redeemCreditAsync.rejected, (state) => {
        state.redeemCredit.status = "failed";
      })
      .addCase(asyncActions.getProductGroupContentAsync.pending, (state) => {
        state.productGroupContent.status = "loading";
      })
      .addCase(
        asyncActions.getProductGroupContentAsync.fulfilled,
        (state, action) => {
          state.productGroupContent = {
            data: action.payload,
            status: "idle",
          };
        }
      )
      .addCase(asyncActions.getProductGroupContentAsync.rejected, (state) => {
        state.productGroupContent.status = "failed";
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
